import {
    Button,
    ButtonBase,
    Checkbox,
    FormControlLabel,
    Grid,
    Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { runInAction } from "mobx";
import { Observer, observer } from "mobx-react";
import React, { FunctionComponent, useState } from "react";
import { DataProcessingOptions } from "../../../../../../models/DataProcesses";
import { OrganizationStructureMember } from "../../../../../../models/OrganizationModels/OrganizationStructureMember";
import { SamplingType } from "../../../../../../services/AudioFilesService";
import AcxMainTextField from "../../../../../UI/AcxMainTextFieldGrid";
import VerticalDrawerContentTemplate from "../../../../../UI/Drawer/VerticalDrawerContentTemplate";
import AcxServiceHierarchySelector from "../../../../../UI/Select/AcxServiceHierarchySelector";
import { UseSampleOp } from "../../../AudioFileSampler/Stores/AudioFileSamplerStore";
import { RecommendationStepStore } from "../../Stores/RecommendationStepStore";
import { useTheme } from "@mui/material";

interface SpecSaveConfirmDialog {
    store: RecommendationStepStore;
}

type Props = SpecSaveConfirmDialog;

const StartSamplingDrawerContent: FunctionComponent<
    React.PropsWithChildren<React.PropsWithChildren<Props>>
> = observer((props: Props) => {
    const [err, setErr] = useState<string | undefined>();
    const orgStore = props.store.orgStore;

    function onStart() {
        try {
            setErr(undefined);
            let ignoreBatching =
                props.store.onlyUseBackgroundSampler ||
                props.store.useBackgroundSampler;
            props.store.useSample(ignoreBatching);
        } catch (e) {
            setErr(`${e}`);
        }
    }

    function removeOption(index: number) {
        const samplingOptionDistributionMap =
            props.store.hierarchyAssignmentDistribution;
        runInAction(() => {
            samplingOptionDistributionMap.delete(index);
        });
    }

    function onAddOption() {
        const samplingOptionDistributionMap =
            props.store.hierarchyAssignmentDistribution;

        const initElement: any = { selections: [], count: "0" };

        const index = Math.max(...samplingOptionDistributionMap.keys()) + 1;

        runInAction(() => {
            samplingOptionDistributionMap.set(index, initElement);
        });
    }

    function onAllotmentChange(evt, index: number) {
        setErr(undefined);
        const existing = props.store.hierarchyAssignmentDistribution.get(
            index,
        ) ?? {
            selections: [],
            count: "0",
        };
        runInAction(() => {
            existing.count = stringToAllotment(evt.currentTarget.value);
            props.store.hierarchyAssignmentDistribution.set(index, existing);
        });
    }

    function onHierarchySelect(
        index: number,
        hierarchyIndex: number,
        newValue: OrganizationStructureMember,
    ) {
        setErr(undefined);
        const existing = props.store.hierarchyAssignmentDistribution.get(
            index,
        ) ?? { selections: [], count: "0" };
        runInAction(() => {
            existing.selections = [
                ...existing.selections
                    .splice(0, hierarchyIndex)
                    .concat([newValue])
                    .filter((value) => value.name !== "No Selection"),
            ];
            props.store.hierarchyAssignmentDistribution.set(index, existing);
        });
    }

    function handleSampleTypeChange(
        event: React.ChangeEvent<HTMLInputElement>,
    ) {
        if (event.target.checked) {
            props.store.setSamplingType(SamplingType.MiddleOfTheFunnel);
        } else {
            props.store.setSamplingType(SamplingType.BottomOfTheFunnel);
        }
    }

    function handleUseBackgroundSamplerChange(
        event: React.ChangeEvent<HTMLInputElement>,
    ) {
        if (event.target.checked) {
            props.store.setBackgroundSampling(true);
        } else {
            props.store.setBackgroundSampling(false);
        }
    }
    const theme = useTheme();

    function getContent() {
        const samplingOptionDistributionMap =
            props.store.hierarchyAssignmentDistribution;

        return (
            <Observer>
                {() => (
                    <div>
                        <Grid container wrap={"nowrap"}>
                            <Grid item>
                                <Button
                                    variant={"contained"}
                                    style={{
                                        textTransform: "none",
                                        fontWeight: "bold",
                                    }}
                                    disableElevation
                                    disabled={props.store.getTaskLoading(
                                        UseSampleOp,
                                    )}
                                    color={"secondary"}
                                    onClick={onStart}
                                >
                                    Start
                                </Button>
                            </Grid>

                            <Grid
                                item
                                container
                                justifyContent={"flex-end"}
                                alignItems={"center"}
                            >
                                {props.store.authStore.isUserUltra() &&
                                    !props.store.onlyUseBackgroundSampler && (
                                        <Grid item>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={
                                                            props.store
                                                                .useBackgroundSampler
                                                        }
                                                        onChange={
                                                            handleUseBackgroundSamplerChange
                                                        }
                                                        name="UseBackgroundSampler"
                                                    />
                                                }
                                                label={
                                                    <Typography
                                                        color={"textSecondary"}
                                                        style={{
                                                            fontWeight: "bold",
                                                            fontSize: "12px",
                                                        }}
                                                        variant={"subtitle2"}
                                                    >
                                                        Use new background
                                                        sampler
                                                    </Typography>
                                                }
                                            />
                                        </Grid>
                                    )}

                                {orgStore.organizationHasDataProcessingOptionEnabled(
                                    DataProcessingOptions.MiddleOfTheFunnel,
                                ) && (
                                    <Grid item>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={
                                                        props.store
                                                            .samplingType ===
                                                        SamplingType.MiddleOfTheFunnel
                                                    }
                                                    onChange={
                                                        handleSampleTypeChange
                                                    }
                                                    name="SampleToMiddleOfTheFunnel"
                                                />
                                            }
                                            label={
                                                <Typography
                                                    color={"textSecondary"}
                                                    style={{
                                                        fontWeight: "bold",
                                                        fontSize: "12px",
                                                    }}
                                                    variant={"subtitle2"}
                                                >
                                                    Sample to Middle of the
                                                    Funnel
                                                </Typography>
                                            }
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        <Typography
                            sx={{
                                fontSize: "13px",
                                color: theme.palette.error.main,
                                textAlign: "left",
                                marginTop: 1,
                                marginBottom: 1,
                            }}
                            component="div"
                        >
                            {err}
                        </Typography>

                        {[...samplingOptionDistributionMap.keys()].map(
                            (i: number) => (
                                <Grid
                                    container
                                    direction={"row"}
                                    justifyContent="flex-start"
                                    sx={{
                                        height: "100%",
                                        width: "100%",
                                        paddingBottom: 1.5,
                                    }}
                                    key={`${i}`}
                                    alignItems="center"
                                >
                                    <Grid
                                        item
                                        xs={12}
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                        }}
                                        container
                                        justifyContent={"flex-start"}
                                        alignItems={"flex-start"}
                                        spacing={1}
                                        wrap={"nowrap"}
                                    >
                                        <Grid item xs>
                                            <AcxMainTextField
                                                value={`${
                                                    samplingOptionDistributionMap.get(
                                                        i,
                                                    )?.count ?? 0
                                                }`}
                                                textItemStyle={{
                                                    minHeight: "34px",
                                                }}
                                                labelText={"Allotment"}
                                                onChange={(evt) =>
                                                    onAllotmentChange(evt, i)
                                                }
                                                id={`callbatch-callcount-${i}`}
                                            />
                                        </Grid>
                                        <Grid item xs>
                                            <AcxServiceHierarchySelector
                                                levels={props.store.levels}
                                                fullWidth
                                                members={props.store.members}
                                                hierarchySelections={
                                                    props.store.hierarchyAssignmentDistribution.get(
                                                        i,
                                                    )?.selections ?? []
                                                }
                                                onSelectMember={(
                                                    hierarchyIndex,
                                                    newValue,
                                                ) =>
                                                    onHierarchySelect(
                                                        i,
                                                        hierarchyIndex,
                                                        newValue,
                                                    )
                                                }
                                                containerRef={document.body}
                                            />
                                        </Grid>

                                        <Grid item xs={1}>
                                            {i > 0 && (
                                                <ButtonBase
                                                    style={{
                                                        marginTop: "24px",
                                                    }}
                                                    onClick={() =>
                                                        removeOption(i)
                                                    }
                                                >
                                                    <CloseIcon
                                                        sx={{
                                                            color: theme.palette
                                                                .text.disabled,
                                                        }}
                                                        fontSize={"small"}
                                                    />
                                                </ButtonBase>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            ),
                        )}

                        <Grid
                            item
                            xs={12}
                            container
                            justifyContent={"flex-start"}
                            style={{ marginTop: "22px" }}
                        >
                            <ButtonBase
                                sx={{
                                    color: theme.palette.secondary.main,
                                    fontWeight: theme.typography
                                        .fontWeightBold as any,
                                    fontSize: "13px",
                                }}
                                onClick={() => onAddOption()}
                            >
                                <AddCircleOutlineIcon
                                    sx={{
                                        marginRight: theme.spacing(1),
                                        fontSize: "14px",
                                    }}
                                    fontSize={"small"}
                                />{" "}
                                Add Assignment
                            </ButtonBase>
                        </Grid>
                    </div>
                )}
            </Observer>
        );
    }

    return (
        <VerticalDrawerContentTemplate
            title={"Initiate Sampling"}
            content={getContent()}
            width={"500px"}
        />
    );
});

export function stringToAllotment(sampleSize: string) {
    sampleSize = (sampleSize ?? "").trim();
    if (sampleSize === "") {
        return sampleSize;
    }

    try {
        let size = parseInt(sampleSize, 10);
        if (Number.isNaN(size) || size < 0) {
            size = 0;
        }

        return size.toString(10);
    } catch (err) {
        return "0";
    }
}

export default StartSamplingDrawerContent;
